import { useState, useEffect, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Package,
  ShoppingCart,
  BarChart3,
  Plus,
  Edit2,
  Trash2,
  Upload,
  Star,
  X,
  Save,
  CheckCircle2,
  Loader2,
  ImageIcon,
  Ticket,
  Users,
  LayoutDashboard,
} from "lucide-react";
import { api, formatINR, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CATEGORIES = [
  "Hair Care",
  "Skin Care",
  "Facial Kits",
  "Wax Products",
  "Serums",
  "Professional Salon Essentials",
];

const STATUS_OPTIONS = [
  { value: "pending_payment", label: "Pending Payment", color: "#C9A961" },
  { value: "paid", label: "Paid", color: "#16a34a" },
  { value: "shipped", label: "Shipped", color: "#2563eb" },
  { value: "delivered", label: "Delivered", color: "#000" },
  { value: "cancelled", label: "Cancelled", color: "#dc2626" },
];

const BACKEND = BACKEND_URL;

function absoluteUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${BACKEND}${url}`;
}

export default function Admin() {
  const { user, loading, loginWithGoogle } = useAuth();
  const [tab, setTab] = useState("products");

  if (loading) {
    return (
      <div className="pt-40 text-center overline" data-testid="admin-loading">
        Checking access…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pt-40 pb-20 min-h-screen text-center" data-testid="admin-login-required">
        <div className="overline mb-3">Restricted</div>
        <h1 className="font-serif-display text-5xl tracking-tighter font-light">
          Sign in to <em className="gold-text not-italic">continue</em>
        </h1>
        <p className="text-sm text-[#5C5C5C] mt-4 max-w-md mx-auto">
          The admin panel is restricted to whitelisted accounts.
        </p>
        <button onClick={loginWithGoogle} className="btn-primary mt-8" data-testid="admin-login-button">
          Continue with Google
        </button>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="pt-40 pb-20 min-h-screen text-center" data-testid="admin-forbidden">
        <div className="overline mb-3 text-red-600">Access Denied</div>
        <h1 className="font-serif-display text-5xl tracking-tighter font-light">
          You're not on the <em className="gold-text not-italic">guest list</em>
        </h1>
        <p className="text-sm text-[#5C5C5C] mt-4 max-w-md mx-auto">
          Your account ({user.email}) is not whitelisted. Ask the owner to add it.
        </p>
        <Link to="/" className="btn-outline mt-8 inline-flex" data-testid="admin-forbidden-home">
          Back to Shop
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-20 min-h-screen bg-white" data-testid="admin-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="overline mb-2">Concierge</div>
            <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter font-light">
              The Atelier <em className="gold-text not-italic">Desk</em>
            </h1>
          </div>
          <div className="text-right">
            <div className="overline">Signed in as</div>
            <div className="font-serif-display text-lg">{user.name}</div>
            <div className="text-xs text-[#5C5C5C]">{user.email}</div>
          </div>
        </div>

        <div className="flex items-center gap-1 border-b border-black/10 mb-10 overflow-x-auto no-scrollbar -mx-2 px-2" data-testid="admin-tabs">
          {[
            { key: "products", label: "Products", icon: Package },
            { key: "orders", label: "Orders", icon: ShoppingCart },
            { key: "salon", label: "Salon Partners", icon: Users },
            { key: "coupons", label: "Coupons", icon: Ticket },
            { key: "categories", label: "Categories", icon: ImageIcon },
            { key: "homepage", label: "Homepage", icon: LayoutDashboard },
            { key: "stats", label: "Overview", icon: BarChart3 },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 md:px-5 py-3 text-[10px] md:text-[11px] tracking-[0.22em] uppercase border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
                tab === t.key
                  ? "border-[#C9A961] text-black"
                  : "border-transparent text-[#5C5C5C] hover:text-black"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              <t.icon size={14} strokeWidth={1.4} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "products" && <ProductsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "salon" && <SalonPartnersTab />}
        {tab === "coupons" && <CouponsTab />}
        {tab === "categories" && <CategoriesTab />}
        {tab === "homepage" && <HomepageTab />}
        {tab === "stats" && <StatsTab />}
      </div>
    </div>
  );
}

/* ---------------- Salon Partners ---------------- */
function SalonPartnersTab() {
  const [view, setView] = useState("partners"); // partners | slabs
  return (
    <div data-testid="admin-salon-partners">
      <div className="flex items-center gap-2 mb-6 border-b border-black/10 pb-3">
        <button
          onClick={() => setView("partners")}
          className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase transition-colors ${view === "partners" ? "bg-black text-white" : "text-[#5C5C5C] hover:text-black"}`}
          data-testid="salon-subview-partners"
        >
          Applications & Partners
        </button>
        <button
          onClick={() => setView("slabs")}
          className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase transition-colors ${view === "slabs" ? "bg-black text-white" : "text-[#5C5C5C] hover:text-black"}`}
          data-testid="salon-subview-slabs"
        >
          Loyalty Slabs
        </button>
      </div>
      {view === "partners" ? <SalonApplicationsTab /> : <SlabDefinitionsTab />}
    </div>
  );
}

function SalonApplicationsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(null); // partner id

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/salon-partners", { params });
      setItems(data);
    } catch (e) {
      toast.error("Could not load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status, note = null) => {
    setBusyId(id);
    try {
      const body = { status };
      if (note != null) body.note = note;
      await api.patch(`/admin/salon-partners/${id}`, body);
      toast.success(`Marked as ${status}`);
      await load();
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch (e) { return iso.slice(0, 10); }
  };
  const fmtRelTime = (iso) => {
    if (!iso) return "Never";
    try {
      const d = new Date(iso);
      const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return "Just now";
      if (diffMin < 60) return `${diffMin}m ago`;
      const h = Math.floor(diffMin / 60);
      if (h < 24) return `${h}h ago`;
      return `${Math.floor(h / 24)}d ago`;
    } catch (e) { return "—"; }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="salon-filter-tabs">
        {[
          { key: "pending", label: "Pending Review" },
          { key: "approved", label: "Approved" },
          { key: "rejected", label: "Rejected" },
          { key: "all", label: "All" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
              filter === f.key ? "bg-black text-white border-black" : "bg-transparent border-black/15 hover:border-black"
            }`}
            data-testid={`salon-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="overline">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-black/15 p-16 text-center text-[#5C5C5C] text-sm" data-testid="salon-empty">
          No applications {filter !== "all" ? `in ${filter} state` : "yet"}.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((s) => (
            <div key={s.id} className="border border-black/10 p-6" data-testid={`salon-row-${s.id}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="font-serif-display text-2xl tracking-tight">{s.salon_name}</h3>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${
                      s.status === "approved" ? "bg-[#C9A961]/20 text-[#9d8345]" :
                      s.status === "rejected" ? "bg-red-50 text-red-700" :
                      "bg-black/5 text-black"
                    }`}>{s.status}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                    <Info label="Owner" value={s.owner_name} />
                    <Info label="Phone" value={s.phone} />
                    <Info label="City" value={s.city} />
                    <Info label="Email" value={s.email} />
                    {s.gst_number && <Info label="GST" value={s.gst_number} mono />}
                    {s.instagram && <Info label="Instagram" value={s.instagram} />}
                    {s.status === "approved" && (
                      <>
                        <Info label="Last Visit" value={fmtRelTime(s.last_visit_at)} />
                        <Info label="Last Viewed" value={s.last_product_viewed_name || "—"} />
                      </>
                    )}
                  </div>
                  <div className="text-[10px] text-[#5C5C5C] mt-3 uppercase tracking-wider">
                    Applied {fmtDate(s.created_at)}
                    {s.approved_at && ` · Approved ${fmtDate(s.approved_at)}`}
                    {s.rejected_at && ` · Rejected ${fmtDate(s.rejected_at)}`}
                  </div>
                  {s.admin_note && (
                    <div className="mt-2 text-xs text-[#5C5C5C] italic">Note: "{s.admin_note}"</div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  {s.status === "approved" && (
                    <button
                      onClick={() => setDetailsOpen(s.id)}
                      className="px-4 py-2 bg-[#C9A961] text-white text-[11px] tracking-[0.22em] uppercase hover:bg-black transition-colors flex items-center gap-2"
                      data-testid={`salon-manage-${s.id}`}
                    >
                      <Ticket size={12} /> Manage / Slab
                    </button>
                  )}
                  {s.status !== "approved" && (
                    <button
                      onClick={() => updateStatus(s.id, "approved")}
                      disabled={busyId === s.id}
                      className="px-4 py-2 bg-black text-white text-[11px] tracking-[0.22em] uppercase hover:bg-[#C9A961] transition-colors disabled:opacity-50 flex items-center gap-2"
                      data-testid={`salon-approve-${s.id}`}
                    >
                      {busyId === s.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                      Approve
                    </button>
                  )}
                  {s.status !== "rejected" && (
                    <button
                      onClick={() => {
                        const note = window.prompt("Reason (optional, sent to applicant):", s.admin_note || "");
                        if (note === null) return;
                        updateStatus(s.id, "rejected", note);
                      }}
                      disabled={busyId === s.id}
                      className="px-4 py-2 border border-black/20 text-[11px] tracking-[0.22em] uppercase hover:border-red-500 hover:text-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                      data-testid={`salon-reject-${s.id}`}
                    >
                      <X size={12} /> Reject
                    </button>
                  )}
                  {s.status === "approved" && (
                    <button
                      onClick={() => updateStatus(s.id, "pending")}
                      className="px-3 py-2 text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] hover:text-black"
                      data-testid={`salon-revoke-${s.id}`}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {detailsOpen && (
        <PartnerDetailsModal partnerId={detailsOpen} onClose={() => { setDetailsOpen(null); load(); }} />
      )}
    </div>
  );
}

function PartnerDetailsModal({ partnerId, onClose }) {
  const [details, setDetails] = useState(null);
  const [slabs, setSlabs] = useState([]);
  const [selectedSlabId, setSelectedSlabId] = useState("");
  const [paymentReceived, setPaymentReceived] = useState(false);
  const [paymentNote, setPaymentNote] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [d, s] = await Promise.all([
        api.get(`/admin/salon-partners/${partnerId}/details`),
        api.get("/admin/slab-definitions"),
      ]);
      setDetails(d.data);
      setSlabs(s.data);
    } catch (e) {
      toast.error("Could not load partner details");
    }
  };
  useEffect(() => { reload(); }, [partnerId]);

  const assignSlab = async () => {
    if (!selectedSlabId) return toast.error("Choose a slab first");
    setBusy(true);
    try {
      await api.post(`/admin/salon-partners/${partnerId}/slab`, {
        slab_id: selectedSlabId,
        payment_received: paymentReceived,
        payment_note: paymentNote || null,
      });
      toast.success("Slab assigned");
      setSelectedSlabId("");
      setPaymentReceived(false);
      setPaymentNote("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign");
    } finally {
      setBusy(false);
    }
  };

  const togglePayment = async () => {
    if (!details?.active_slab) return;
    try {
      await api.patch(`/admin/slab-assignments/${details.active_slab.id}`, {
        payment_received: !details.active_slab.payment_received,
      });
      await reload();
    } catch (e) { toast.error("Update failed"); }
  };

  const cancelSlab = async () => {
    if (!details?.active_slab) return;
    if (!window.confirm("Cancel this active slab?")) return;
    try {
      await api.patch(`/admin/slab-assignments/${details.active_slab.id}`, { status: "cancelled" });
      toast.success("Slab cancelled");
      await reload();
    } catch (e) { toast.error("Update failed"); }
  };

  if (!details) return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-6"><div className="bg-white p-8 overline">Loading…</div></div>
  );

  const p = details.partner;
  const purchases = details.purchases || {};
  const active = details.active_slab;
  const consumed = details.active_slab_mrp_consumed || 0;
  const accessAmt = active?.mrp_access_amount || 0;
  const pct = accessAmt > 0 ? Math.min(100, Math.round((consumed / accessAmt) * 100)) : 0;

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-6 overflow-y-auto" data-testid="partner-details-modal">
      <div className="bg-white w-full max-w-3xl my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 md:px-7 py-4 md:py-5 border-b border-black/10">
          <div>
            <div className="overline mb-1">Partner Profile</div>
            <h3 className="font-serif-display text-2xl tracking-tight">{p.salon_name}</h3>
            <div className="text-xs text-[#5C5C5C] mt-0.5">{p.owner_name} · {p.phone} · {p.city}</div>
          </div>
          <button onClick={onClose} className="p-2" data-testid="partner-details-close"><X size={18} /></button>
        </div>

        <div className="px-5 md:px-7 py-5 md:py-6 space-y-6 md:space-y-7">
          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total Paid" value={`₹${(purchases.total_paid || 0).toLocaleString("en-IN")}`} />
            <Stat label="Orders" value={purchases.orders_count || 0} />
            <Stat label="Last Visit" value={p.last_visit_at ? new Date(p.last_visit_at).toLocaleDateString("en-IN") : "—"} />
            <Stat label="Last Viewed" value={p.last_product_viewed_name ? p.last_product_viewed_name.slice(0, 18) : "—"} />
          </div>

          {/* Lifetime slab stats */}
          {details.lifetime && details.lifetime.slabs_taken > 0 && (
            <div className="border border-[#C9A961]/40 bg-[#F8F5F0]/30 p-5" data-testid="lifetime-slab-stats">
              <div className="overline text-[#C9A961] mb-3">Lifetime Slab Stats</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Slabs Taken</div>
                  <div className="font-serif-display text-2xl">{details.lifetime.slabs_taken}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Lifetime Paid</div>
                  <div className="font-serif-display text-2xl">₹{(details.lifetime.lifetime_payable || 0).toLocaleString("en-IN")}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">MRP Unlocked</div>
                  <div className="font-serif-display text-2xl">₹{(details.lifetime.lifetime_mrp_access || 0).toLocaleString("en-IN")}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Partner Benefit</div>
                  <div className="font-serif-display text-2xl text-[#C9A961]">₹{(details.lifetime.lifetime_benefit || 0).toLocaleString("en-IN")}</div>
                </div>
              </div>
            </div>
          )}

          {/* Current slab */}
          {active ? (
            <div className="border border-[#C9A961]/40 bg-gradient-to-br from-[#F8F5F0]/50 to-white p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="overline text-[#C9A961]">Active Slab · {(active.tier || "silver").toUpperCase()}</div>
                  <div className="font-serif-display text-2xl tracking-tight">{active.slab_name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C5C5C] mt-1">
                    {active.starts_at?.slice(0, 10)} → {active.expires_at?.slice(0, 10)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={togglePayment} className="text-[10px] tracking-[0.22em] uppercase border border-black/15 px-3 py-1.5 hover:border-black" data-testid="slab-toggle-payment">
                    {active.payment_received ? "✓ Paid" : "Mark Paid"}
                  </button>
                  <button onClick={cancelSlab} className="text-[10px] tracking-[0.22em] uppercase border border-black/15 px-3 py-1.5 hover:border-red-500 hover:text-red-600" data-testid="slab-cancel-active">
                    Cancel
                  </button>
                </div>
              </div>
              <div className="text-xs text-[#5C5C5C] mb-1.5 flex items-center justify-between">
                <span>MRP consumed</span>
                <span>₹{consumed.toLocaleString("en-IN")} / ₹{accessAmt.toLocaleString("en-IN")} ({pct}%)</span>
              </div>
              <div className="h-2 bg-black/10">
                <div className="h-full bg-[#C9A961] transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-black/15 p-5 text-center text-xs text-[#5C5C5C]">
              No active slab assigned.
            </div>
          )}

          {/* Assign new slab */}
          <div className="border border-black/10 p-5" data-testid="assign-slab-section">
            <div className="overline mb-3">{active ? "Replace with New Slab" : "Assign Slab"}</div>
            {slabs.length === 0 ? (
              <div className="text-xs text-[#5C5C5C]">No slab definitions yet. Create one in the Loyalty Slabs tab first.</div>
            ) : (
              <div className="space-y-3">
                <select
                  value={selectedSlabId}
                  onChange={(e) => setSelectedSlabId(e.target.value)}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  data-testid="assign-slab-select"
                >
                  <option value="">— choose a slab —</option>
                  {slabs.filter((s) => s.active).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.tier} · Pay ₹{s.payable_amount.toLocaleString("en-IN")} → Access ₹{s.mrp_access_amount.toLocaleString("en-IN")} · {s.time_period_days}d
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={paymentReceived} onChange={(e) => setPaymentReceived(e.target.checked)} data-testid="assign-payment-received" />
                    <span className="text-xs">Payment received already</span>
                  </label>
                  <input
                    type="text"
                    value={paymentNote}
                    onChange={(e) => setPaymentNote(e.target.value)}
                    placeholder="Payment note (optional)"
                    className="flex-1 min-w-[160px] bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-1.5 text-xs"
                    data-testid="assign-payment-note"
                  />
                </div>
                <button onClick={assignSlab} disabled={busy || !selectedSlabId} className="btn-primary w-full disabled:opacity-50" data-testid="assign-slab-submit">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {busy ? "Assigning…" : "Assign Slab"}
                </button>
              </div>
            )}
          </div>

          {/* History */}
          {details.slab_history?.length > 0 && (
            <div>
              <div className="overline mb-3">Slab History</div>
              <div className="border border-black/10 divide-y divide-black/5 text-xs">
                {details.slab_history.map((h) => (
                  <div key={h.id} className="px-4 py-2.5 flex items-center justify-between" data-testid={`slab-history-${h.id}`}>
                    <div>
                      <span className="font-mono">{h.tier?.toUpperCase()}</span> · {h.slab_name} ·
                      <span className="text-[#5C5C5C] ml-1">{h.starts_at?.slice(0,10)} → {h.expires_at?.slice(0,10)}</span>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider ${
                      h.status === "active" ? "text-[#C9A961]" :
                      h.status === "cancelled" ? "text-red-600" : "text-[#5C5C5C]"
                    }`}>{h.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Order history */}
          {details.orders?.length > 0 && (
            <div data-testid="partner-order-history">
              <div className="overline mb-3">Recent Orders ({details.orders.length})</div>
              <div className="border border-black/10 divide-y divide-black/5 text-xs max-h-72 overflow-y-auto">
                {details.orders.map((o) => (
                  <div key={o.order_id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`partner-order-${o.order_id}`}>
                    <div className="min-w-0 flex-1">
                      <span className="font-mono">{o.order_id}</span>
                      <span className="text-[#5C5C5C] ml-2">{o.created_at?.slice(0, 10)}</span>
                      <span className="text-[#5C5C5C] ml-2">· {o.items?.length || 0} items</span>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="font-medium">₹{Number(o.total || 0).toLocaleString("en-IN")}</span>
                      <span className={`ml-2 text-[10px] uppercase tracking-wider ${
                        o.status === "paid" || o.status === "delivered" ? "text-[#C9A961]" :
                        o.status === "shipped" ? "text-blue-600" : "text-[#5C5C5C]"
                      }`}>{o.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-black/10 px-4 py-3">
      <div className="overline text-[10px]">{label}</div>
      <div className="font-serif-display text-xl mt-0.5">{value}</div>
    </div>
  );
}

function SlabDefinitionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | slab object
  const [busy, setBusy] = useState(false);

  const empty = { name: "", tier: "silver", payable_amount: "", mrp_access_amount: "", time_period_days: 30, description: "", active: true };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/slab-definitions");
      setItems(data);
    } catch (e) {
      toast.error("Could not load slabs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const f = editing;
    if (!f.name || !f.payable_amount || !f.mrp_access_amount || !f.time_period_days) {
      return toast.error("All fields required");
    }
    setBusy(true);
    try {
      const payload = {
        name: f.name.trim(),
        tier: f.tier || "silver",
        payable_amount: Number(f.payable_amount),
        mrp_access_amount: Number(f.mrp_access_amount),
        time_period_days: Number(f.time_period_days),
        description: f.description || null,
        active: !!f.active,
      };
      if (f.id) {
        await api.patch(`/admin/slab-definitions/${f.id}`, payload);
      } else {
        await api.post("/admin/slab-definitions", payload);
      }
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this slab definition?")) return;
    try {
      await api.delete(`/admin/slab-definitions/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-slab-definitions">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline">Loyalty Slabs</div>
          <p className="text-xs text-[#5C5C5C] mt-1 max-w-md">
            Templates you assign to salon partners. Partner pays the "payable amount" and gets access to that much MRP value within the time window.
          </p>
        </div>
        <button onClick={() => setEditing(empty)} className="btn-primary" data-testid="slab-def-new">
          <Plus size={14} /> New Slab
        </button>
      </div>

      {loading ? <div className="overline">Loading…</div> : items.length === 0 ? (
        <div className="border border-dashed border-black/15 p-16 text-center text-sm text-[#5C5C5C]" data-testid="slab-def-empty">
          No slab templates yet. Create your first Silver / Gold / Platinum slab.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {items.map((s) => {
            const tierStyles = {
              silver: "border-[#BFBFBF] bg-gradient-to-br from-[#E5E4E2]/40 to-white",
              gold: "border-[#C9A961] bg-gradient-to-br from-[#F8F5F0] to-white",
              platinum: "border-black bg-gradient-to-br from-black to-[#1f1f1f] text-white",
            };
            const isDark = s.tier === "platinum";
            return (
              <div key={s.id} className={`border-2 ${tierStyles[s.tier] || tierStyles.silver} p-5`} data-testid={`slab-def-${s.id}`}>
                <div className={`flex items-center justify-between mb-2 ${isDark ? "text-white" : ""}`}>
                  <div className="overline">★ {s.tier.toUpperCase()}</div>
                  {!s.active && <span className="text-[10px] uppercase opacity-60">Paused</span>}
                </div>
                <h3 className="font-serif-display text-2xl tracking-tight mb-3">{s.name}</h3>
                <div className={`grid grid-cols-2 gap-3 text-xs mb-4 ${isDark ? "text-white/80" : "text-[#5C5C5C]"}`}>
                  <div>
                    <div className="opacity-70">Payable</div>
                    <div className={`text-base font-medium ${isDark ? "text-white" : "text-black"}`}>₹{s.payable_amount.toLocaleString("en-IN")}</div>
                  </div>
                  <div>
                    <div className="opacity-70">MRP Access</div>
                    <div className={`text-base font-medium ${isDark ? "text-white" : "text-black"}`}>₹{s.mrp_access_amount.toLocaleString("en-IN")}</div>
                  </div>
                  <div>
                    <div className="opacity-70">Duration</div>
                    <div className={`text-base font-medium ${isDark ? "text-white" : "text-black"}`}>{s.time_period_days} days</div>
                  </div>
                  <div>
                    <div className="opacity-70">Margin</div>
                    <div className={`text-base font-medium ${isDark ? "text-white" : "text-black"}`}>
                      {Math.round(((s.mrp_access_amount - s.payable_amount) / s.payable_amount) * 100)}%
                    </div>
                  </div>
                </div>
                {s.description && <div className={`text-xs italic mb-3 ${isDark ? "text-white/70" : "text-[#5C5C5C]"}`}>"{s.description}"</div>}
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditing(s)} className={`text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 border ${isDark ? "border-white/30 hover:border-white" : "border-black/15 hover:border-black"} transition-colors`} data-testid={`slab-def-edit-${s.id}`}>
                    <Edit2 size={11} className="inline mr-1" /> Edit
                  </button>
                  <button onClick={() => remove(s.id)} className={`text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 ${isDark ? "text-white/60 hover:text-red-300" : "text-[#5C5C5C] hover:text-red-600"}`} data-testid={`slab-def-delete-${s.id}`}>
                    <Trash2 size={11} className="inline mr-1" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="slab-def-editor">
          <div className="bg-white w-full max-w-lg p-5 md:p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="overline mb-1">{editing.id ? "Edit Slab" : "New Slab"}</div>
                <h3 className="font-serif-display text-2xl tracking-tight">Loyalty Definition</h3>
              </div>
              <button onClick={() => setEditing(null)} className="p-2"><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <label className="block">
                <div className="overline mb-1">Slab Name</div>
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  placeholder="e.g. Gold Pro"
                  data-testid="slab-form-name"
                />
              </label>

              <label className="block">
                <div className="overline mb-1">Tier Category</div>
                <select
                  value={editing.tier}
                  onChange={(e) => setEditing({ ...editing, tier: e.target.value })}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  data-testid="slab-form-tier"
                >
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="platinum">Platinum</option>
                </select>
              </label>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <div className="overline mb-1">Payable Amount (₹)</div>
                  <input
                    type="number"
                    value={editing.payable_amount}
                    onChange={(e) => setEditing({ ...editing, payable_amount: e.target.value })}
                    className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                    placeholder="25000"
                    data-testid="slab-form-payable"
                  />
                </label>
                <label className="block">
                  <div className="overline mb-1">MRP Access (₹)</div>
                  <input
                    type="number"
                    value={editing.mrp_access_amount}
                    onChange={(e) => setEditing({ ...editing, mrp_access_amount: e.target.value })}
                    className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                    placeholder="55000"
                    data-testid="slab-form-mrp"
                  />
                </label>
              </div>

              <label className="block">
                <div className="overline mb-1">Time Period (Days)</div>
                <input
                  type="number"
                  value={editing.time_period_days}
                  onChange={(e) => setEditing({ ...editing, time_period_days: e.target.value })}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  placeholder="30, 60, 90, 180"
                  data-testid="slab-form-days"
                />
                <div className="text-[10px] text-[#5C5C5C] mt-1 uppercase tracking-wider">
                  e.g. 30 = 1 month · 60 = 2 months · 90 = 3 months · 180 = 6 months
                </div>
              </label>

              <label className="block">
                <div className="overline mb-1">Description (Optional)</div>
                <input
                  type="text"
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  placeholder="Internal note about this slab"
                  data-testid="slab-form-description"
                />
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  data-testid="slab-form-active"
                />
                <span className="text-sm">Active (available for assignment)</span>
              </label>

              <div className="flex items-center gap-3 pt-3">
                <button onClick={() => setEditing(null)} className="btn-outline flex-1" data-testid="slab-form-cancel">Cancel</button>
                <button onClick={save} disabled={busy} className="btn-primary flex-1 disabled:opacity-50" data-testid="slab-form-save">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {busy ? "Saving…" : "Save Slab"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, mono = false }) {
  return (
    <div>
      <div className="overline mb-0.5 text-[10px]">{label}</div>
      <div className={mono ? "font-mono text-xs" : "text-sm"}>{value || "—"}</div>
    </div>
  );
}

/* ---------------- Coupons ---------------- */
function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ code: "", amount_off: "", active: true });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data);
    } catch (e) {
      toast.error("Could not load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    const code = (form.code || "").trim().toUpperCase();
    const amount = parseFloat(form.amount_off);
    if (!code) return toast.error("Code required");
    if (!amount || amount <= 0) return toast.error("Amount must be > 0");
    setBusy(true);
    try {
      await api.post("/admin/coupons", { code, amount_off: amount, active: form.active });
      toast.success(`Coupon ${code} saved`);
      setForm({ code: "", amount_off: "", active: true });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await api.delete(`/admin/coupons/${encodeURIComponent(code)}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const toggle = async (c) => {
    try {
      await api.post("/admin/coupons", { code: c.code, amount_off: c.amount_off, active: !c.active });
      await load();
    } catch (e) {
      toast.error("Could not toggle");
    }
  };

  return (
    <div data-testid="admin-coupons">
      <div className="grid lg:grid-cols-12 gap-8">
        {/* Create form */}
        <form onSubmit={save} className="lg:col-span-4 border border-black/10 p-6 bg-[#F8F5F0]/40 space-y-4 h-fit" data-testid="coupon-create-form">
          <div>
            <div className="overline mb-3">Create / Update</div>
            <h3 className="font-serif-display text-2xl tracking-tight">Discount Code</h3>
            <p className="text-xs text-[#5C5C5C] mt-2">Flat ₹ off on subtotal. Codes are auto-uppercased and unique.</p>
          </div>
          <label className="block">
            <div className="overline mb-1">Code</div>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="BLISSON100"
              className="w-full bg-white border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm uppercase tracking-wider font-mono"
              data-testid="coupon-form-code"
            />
          </label>
          <label className="block">
            <div className="overline mb-1">Amount Off (₹)</div>
            <input
              type="number"
              min="1"
              step="1"
              value={form.amount_off}
              onChange={(e) => setForm({ ...form, amount_off: e.target.value })}
              placeholder="100"
              className="w-full bg-white border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm"
              data-testid="coupon-form-amount"
            />
          </label>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="coupon-form-active-label">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="coupon-form-active"
            />
            <span className="text-sm">Active immediately</span>
          </label>
          <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="coupon-form-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {busy ? "Saving…" : "Save Coupon"}
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-8" data-testid="coupon-list">
          <div className="overline mb-4">All Codes</div>
          {loading ? (
            <div className="overline">Loading…</div>
          ) : coupons.length === 0 ? (
            <div className="border border-dashed border-black/15 p-10 text-center text-[#5C5C5C] text-sm" data-testid="coupon-empty">
              No discount codes yet. Create your first one →
            </div>
          ) : (
            <div className="border border-black/10 divide-y divide-black/5">
              {coupons.map((c) => (
                <div key={c.code} className="flex items-center justify-between px-5 py-4" data-testid={`coupon-row-${c.code}`}>
                  <div>
                    <div className="font-mono uppercase tracking-wider text-base flex items-center gap-3">
                      {c.code}
                      {!c.active && <span className="text-[10px] uppercase bg-black/5 text-[#5C5C5C] px-2 py-0.5">paused</span>}
                    </div>
                    <div className="text-xs text-[#5C5C5C] mt-1">₹{c.amount_off} off · added {c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN") : "—"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggle(c)}
                      className="text-[11px] uppercase tracking-wider border border-black/15 px-3 py-1.5 hover:border-black transition-colors"
                      data-testid={`coupon-toggle-${c.code}`}
                    >
                      {c.active ? "Pause" : "Resume"}
                    </button>
                    <button
                      onClick={() => remove(c.code)}
                      className="p-2 text-[#5C5C5C] hover:text-red-600 transition-colors"
                      data-testid={`coupon-delete-${c.code}`}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Categories ---------------- */
function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // category currently open
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/category-meta");
      setItems(data);
    } catch (e) {
      toast.error("Could not load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return null; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    } catch (e) {
      toast.error("Upload failed"); return null;
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/admin/category-meta/${encodeURIComponent(editing.name)}`, {
        name: editing.name,
        tagline: editing.tagline || "",
        image: editing.image || "",
        display_order: Number(editing.display_order) || 9999,
      });
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="overline mb-4">Homepage Category Cards</div>
      <p className="text-xs text-[#5C5C5C] mb-6 max-w-2xl">
        Edit the image and tagline shown on the Home page "A ritual for every facet of you" section. Lower display order shows first.
      </p>

      {loading ? (
        <div className="overline">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((c) => (
            <div key={c.name} className="border border-black/10 overflow-hidden group" data-testid={`cat-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="aspect-[4/3] bg-[#F8F5F0] overflow-hidden">
                {c.image ? (
                  <img src={c.image.startsWith("http") || c.image.startsWith("data:") ? c.image : `${BACKEND_URL}${c.image}`} alt={c.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#5C5C5C] text-xs uppercase tracking-wider">No image</div>
                )}
              </div>
              <div className="p-5">
                <div className="font-serif-display text-xl">{c.name}</div>
                <div className="text-xs text-[#5C5C5C] mt-1 italic">"{c.tagline || "—"}"</div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Order #{c.display_order || "—"}</span>
                  <button
                    onClick={() => setEditing({ ...c })}
                    className="text-[11px] uppercase tracking-wider border border-black/15 px-3 py-1.5 hover:border-black transition-colors flex items-center gap-1.5"
                    data-testid={`cat-edit-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Edit2 size={12} /> Edit
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6" data-testid="cat-edit-modal">
          <div className="bg-white w-full max-w-xl p-5 md:p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline mb-1">Edit Category</div>
                <h3 className="font-serif-display text-3xl tracking-tight">{editing.name}</h3>
              </div>
              <button onClick={() => setEditing(null)} className="p-2"><X size={18} /></button>
            </div>

            <div className="space-y-5">
              <label className="block">
                <div className="overline mb-2">Tagline</div>
                <input
                  type="text"
                  value={editing.tagline || ""}
                  onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  data-testid="cat-edit-tagline"
                  placeholder="e.g. Silk-strand restoration"
                />
              </label>

              <label className="block">
                <div className="overline mb-2">Display Order (lower = first)</div>
                <input
                  type="number"
                  value={editing.display_order ?? 9999}
                  onChange={(e) => setEditing({ ...editing, display_order: e.target.value })}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  data-testid="cat-edit-order"
                />
              </label>

              <div>
                <div className="overline mb-2">Image</div>
                {editing.image && (
                  <div className="aspect-[3/2] bg-[#F8F5F0] mb-3 overflow-hidden">
                    <img src={editing.image.startsWith("http") || editing.image.startsWith("data:") ? editing.image : `${BACKEND_URL}${editing.image}`} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="px-4 py-2 border border-black/15 text-[11px] uppercase tracking-wider hover:bg-black hover:text-white transition-colors cursor-pointer inline-flex items-center gap-2" data-testid="cat-edit-upload">
                    {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    Upload Image
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const url = await upload(f);
                        if (url) setEditing({ ...editing, image: url });
                      }}
                    />
                  </label>
                  <input
                    type="text"
                    value={editing.image || ""}
                    onChange={(e) => setEditing({ ...editing, image: e.target.value })}
                    placeholder="…or paste image URL"
                    className="flex-1 min-w-[200px] bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-xs"
                    data-testid="cat-edit-image-url"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button onClick={() => setEditing(null)} className="btn-outline flex-1" data-testid="cat-edit-cancel">Cancel</button>
                <button onClick={save} disabled={saving} className="btn-primary flex-1 disabled:opacity-50" data-testid="cat-edit-save">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Homepage (editable Hero) ---------------- */
function HomepageTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api
      .get("/site/hero")
      .then((r) => setForm(r.data))
      .catch(() => toast.error("Could not load hero settings"));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("image", data.url);
      toast.success("Hero image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/hero", form);
      toast.success("Homepage updated. Refresh the site to see live.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="overline" data-testid="admin-homepage-loading">Loading…</div>;

  const field = (key, label, opts = {}) => (
    <label className="block" key={key}>
      <span className="overline text-[10px] block mb-1.5">{label}</span>
      {opts.multiline ? (
        <textarea
          rows={opts.rows || 3}
          value={form[key] || ""}
          onChange={(e) => update(key, e.target.value)}
          className="w-full bg-white border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm resize-y"
          data-testid={`hero-field-${key}`}
        />
      ) : (
        <input
          type="text"
          value={form[key] || ""}
          onChange={(e) => update(key, e.target.value)}
          placeholder={opts.placeholder || ""}
          className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
          data-testid={`hero-field-${key}`}
        />
      )}
    </label>
  );

  return (
    <div className="space-y-8 max-w-4xl" data-testid="admin-homepage-tab">
      <div>
        <div className="overline text-[#C9A961] mb-1">Homepage Hero</div>
        <h2 className="font-serif-display text-3xl tracking-tight">Edit the top of your homepage.</h2>
        <p className="text-xs text-[#5C5C5C] mt-2">
          Hero image, headline, description, CTAs, and the floating "Best Seller" card on the right —
          all live-editable. Save changes and refresh blisson.in to see them go live.
        </p>
      </div>

      {/* Hero Image */}
      <section className="border border-black/10 p-5 md:p-6 bg-[#F8F5F0]/30">
        <div className="overline mb-3">Hero Background Image</div>
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-40 h-52 bg-white border border-black/10 overflow-hidden flex-shrink-0">
            {form.image ? (
              <img src={absoluteUrl(form.image)} alt="Hero" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[10px] text-[#5C5C5C] text-center px-3">
                Using default<br />HERO_IMG
              </div>
            )}
          </div>
          <div className="flex-1 min-w-[250px] space-y-3">
            <label className="block">
              <span className="overline text-[10px] block mb-1.5">Image URL (or upload below)</span>
              <input
                type="text"
                value={form.image || ""}
                onChange={(e) => update("image", e.target.value)}
                placeholder="https://… or upload"
                className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                data-testid="hero-field-image"
              />
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="btn-outline cursor-pointer inline-flex" data-testid="hero-image-upload-label">
                <Upload size={14} />
                {uploading ? "Uploading…" : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onFile}
                  disabled={uploading}
                  data-testid="hero-image-upload"
                />
              </label>
              {form.image && (
                <button
                  type="button"
                  onClick={() => update("image", "")}
                  className="text-xs text-[#5C5C5C] underline hover:text-red-600"
                  data-testid="hero-image-clear"
                >
                  Remove (use default)
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Headline & Description */}
      <section className="border border-black/10 p-5 md:p-6">
        <div className="overline mb-4">Headline & Description</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {field("overline", "Overline tag", { placeholder: "The Salon Edit · No. 01" })}
          {field("heading_line_1", "Heading line 1", { placeholder: "Professional" })}
          {field("heading_line_2", "Heading line 2 (italic gold)", { placeholder: "Salon Care" })}
          {field("heading_line_3", "Heading line 3", { placeholder: "at Home." })}
        </div>
        <div className="mt-4">
          {field("description", "Sub-paragraph", { multiline: true, rows: 3 })}
        </div>
      </section>

      {/* CTAs */}
      <section className="border border-black/10 p-5 md:p-6">
        <div className="overline mb-4">Call-to-Action Buttons</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {field("primary_cta_label", "Primary button label", { placeholder: "Shop Now" })}
          {field("primary_cta_link", "Primary button link", { placeholder: "/shop" })}
          {field("secondary_cta_label", "Secondary button label", { placeholder: "Explore Collection" })}
          {field("secondary_cta_link", "Secondary button link", { placeholder: "/shop" })}
        </div>
      </section>

      {/* Floating "Best Seller" Card */}
      <section className="border-2 border-[#C9A961]/40 bg-gradient-to-br from-[#F8F5F0]/40 to-transparent p-5 md:p-6">
        <div className="overline text-[#C9A961] mb-1">★ Floating Best Seller Card</div>
        <div className="text-[11px] text-[#5C5C5C] mb-4">
          The dark glass card overlaid on the hero image (currently shows "24K Gold Radiance Facial Kit").
          Leave the title blank to hide the card completely.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {field("card_label", "Card label (small tag)", { placeholder: "Best Seller" })}
          {field("card_title", "Card title", { placeholder: "24K Gold Radiance Facial Kit" })}
          {field("card_price", "Price text", { placeholder: "From ₹2,899" })}
          {field("card_cta_label", "CTA label", { placeholder: "Discover" })}
          {field("card_cta_link", "CTA link", { placeholder: "/shop or /product/p_abc123" })}
        </div>
      </section>

      {/* Stats */}
      <section className="border border-black/10 p-5 md:p-6">
        <div className="overline mb-4">Stats row (3 stats below the headline)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-3">
            {field("stat_1_value", "Stat 1 value", { placeholder: "12+" })}
            {field("stat_1_label", "Stat 1 label", { placeholder: "Salon Awards" })}
          </div>
          <div className="space-y-3">
            {field("stat_2_value", "Stat 2 value", { placeholder: "1.4k" })}
            {field("stat_2_label", "Stat 2 label", { placeholder: "Salons Trust Us" })}
          </div>
          <div className="space-y-3">
            {field("stat_3_value", "Stat 3 value", { placeholder: "98%" })}
            {field("stat_3_label", "Stat 3 label", { placeholder: "Repeat Clients" })}
          </div>
        </div>
        <div className="text-[10px] text-[#5C5C5C] mt-3">Leave a value blank to hide that stat tile.</div>
      </section>

      {/* Before / After */}
      <section className="border border-black/10 p-5 md:p-6 bg-[#F8F5F0]/30" data-testid="admin-before-after">
        <div className="overline mb-1">Before / After Slider Images</div>
        <div className="text-[11px] text-[#5C5C5C] mb-4">
          The homepage "Real Results" slider. Upload the client's photo before treatment and after treatment.
          Recommended size: 1200×1500 (portrait) or 1200×960 (landscape). Blank → uses defaults.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {["before", "after"].map((slot) => {
            const key = `before_after_${slot}_image`;
            const label = slot === "before" ? "Before" : "After";
            const onFile = async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
              const fd = new FormData();
              fd.append("file", file);
              try {
                const { data } = await api.post("/admin/upload", fd, {
                  headers: { "Content-Type": "multipart/form-data" },
                });
                update(key, data.url);
                toast.success(`${label} image uploaded`);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Upload failed");
              } finally {
                e.target.value = "";
              }
            };
            return (
              <div key={slot} data-testid={`ba-slot-${slot}`}>
                <div className="overline text-[10px] mb-2">{label}</div>
                <div className="aspect-[4/5] bg-white border border-black/10 overflow-hidden mb-3">
                  {form[key] ? (
                    <img src={absoluteUrl(form[key])} alt={label} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-[#5C5C5C] text-center px-3">
                      Using default<br />(Unsplash)
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  value={form[key] || ""}
                  onChange={(e) => update(key, e.target.value)}
                  placeholder="https://… or upload"
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm mb-2"
                  data-testid={`ba-url-${slot}`}
                />
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="btn-outline cursor-pointer inline-flex" data-testid={`ba-upload-${slot}`}>
                    <Upload size={14} /> Upload {label}
                    <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                  </label>
                  {form[key] && (
                    <button
                      type="button"
                      onClick={() => update(key, "")}
                      className="text-xs text-[#5C5C5C] underline hover:text-red-600"
                      data-testid={`ba-clear-${slot}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="sticky bottom-3 z-10 flex items-center justify-end gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary disabled:opacity-60"
          data-testid="homepage-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save Homepage"}
        </button>
      </div>
    </div>
  );
}


/* ---------------- Stats ---------------- */
function StatsTab() {
  const [stats, setStats] = useState(null);
  const [bcOpen, setBcOpen] = useState(false);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);
  if (!stats) return <div className="overline" data-testid="admin-stats-loading">Loading…</div>;
  const cards = [
    { label: "Products", value: stats.products },
    { label: "Total Orders", value: stats.orders },
    { label: "Pending", value: stats.pending_orders },
    { label: "Revenue", value: formatINR(stats.revenue) },
  ];
  return (
    <div data-testid="admin-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => (
          <div key={c.label} className="border border-black/10 p-5 md:p-7 bg-[#F8F5F0]/40" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="overline mb-2">{c.label}</div>
            <div className="font-serif-display text-3xl md:text-4xl">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Send Announcement */}
      <div className="mt-10 border border-black/10 p-5 md:p-7 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline mb-1">Push Notifications</div>
            <h3 className="font-serif-display text-2xl tracking-tight">Send announcement to all subscribers</h3>
            <p className="text-xs text-[#5C5C5C] mt-1 max-w-lg">
              Notifications also fire automatically when a new product is added or a product's discount goes above 30%.
            </p>
          </div>
          <button onClick={() => setBcOpen(true)} className="btn-primary" data-testid="open-broadcast">
            ✉ Send Announcement
          </button>
        </div>
      </div>

      {bcOpen && <BroadcastModal onClose={() => setBcOpen(false)} />}
    </div>
  );
}

function BroadcastModal({ onClose }) {
  const [form, setForm] = useState({ title: "", body: "", url: "/" });
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push/broadcast", {
        title: form.title.trim(),
        body: form.body.trim(),
        url: form.url || "/",
      });
      toast.success(`Sent to ${data.sent} / ${data.requested} subscribers`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="broadcast-modal">
      <div className="bg-white w-full max-w-lg p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="overline mb-1">Push Broadcast</div>
            <h3 className="font-serif-display text-2xl tracking-tight">New Announcement</h3>
          </div>
          <button onClick={onClose} className="p-2"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <div className="overline mb-1">Title</div>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={60}
              placeholder="Diwali Sale starts today 🌟"
              className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
              data-testid="broadcast-title"
            />
            <div className="text-[10px] text-[#5C5C5C] mt-1 text-right">{form.title.length}/60</div>
          </label>
          <label className="block">
            <div className="overline mb-1">Message</div>
            <textarea
              rows={3}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              maxLength={140}
              placeholder="Flat 30% off across luxury hair care. Tap to shop."
              className="w-full bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm"
              data-testid="broadcast-body"
            />
            <div className="text-[10px] text-[#5C5C5C] mt-1 text-right">{form.body.length}/140</div>
          </label>
          <label className="block">
            <div className="overline mb-1">Click destination URL</div>
            <input
              type="text"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="/shop or /product/xxx"
              className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
              data-testid="broadcast-url"
            />
          </label>

          <div className="flex items-center gap-3 pt-3">
            <button onClick={onClose} className="btn-outline flex-1">Cancel</button>
            <button onClick={send} disabled={busy} className="btn-primary flex-1 disabled:opacity-50" data-testid="broadcast-send">
              {busy ? <Loader2 size={12} className="animate-spin" /> : null}
              {busy ? "Sending…" : "Send Now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Products ---------------- */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product object or "new"
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/products");
      setProducts(r.data);
    } catch (e) {
      toast.error("Could not load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!confirm("Delete this product? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product removed.");
      load();
    } catch (e) {
      toast.error("Could not delete product.");
    }
  };

  const visible = filter === "All" ? products : products.filter((p) => p.category === filter);

  return (
    <div data-testid="admin-products-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase whitespace-nowrap border ${
                filter === c ? "bg-black text-white border-black" : "border-black/15 hover:border-black"
              }`}
              data-testid={`admin-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
        <button
          onClick={() => setEditing("new")}
          className="btn-primary"
          data-testid="admin-new-product-button"
        >
          <Plus size={14} /> New Product
        </button>
      </div>

      {loading ? (
        <div className="overline" data-testid="admin-products-loading">Loading…</div>
      ) : (
        <div className="border border-black/5 overflow-x-auto -mx-2 px-2" data-testid="admin-products-table">
          <div className="min-w-[640px]">
            <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-[#F8F5F0] overline text-[10px]">
              <div className="col-span-5">Product</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Price</div>
              <div className="col-span-1">Stock</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {visible.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-[#5C5C5C]" data-testid="admin-products-empty">
                No products yet. Click "New Product" to add your first one.
              </div>
            )}
            {visible.map((p) => (
              <div
                key={p.id}
                className="grid grid-cols-12 gap-3 px-5 py-4 border-t border-black/5 items-center hover:bg-[#F8F5F0]/40"
                data-testid={`admin-product-row-${p.id}`}
              >
              <div className="col-span-5 flex items-center gap-3">
                <div className="w-14 h-16 bg-[#F8F5F0] flex-shrink-0 overflow-hidden">
                  {p.image ? (
                    <img src={absoluteUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#C9A961]">
                      <ImageIcon size={20} strokeWidth={1.2} />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-serif-display text-base leading-tight truncate">{p.name}</div>
                  <div className="text-[11px] text-[#5C5C5C] mt-0.5 flex items-center gap-2 flex-wrap">
                    {p.bestseller && <span className="text-[#C9A961]">★ Bestseller</span>}
                    {p.new_arrival && <span className="text-[#C9A961]">New</span>}
                    {p.display_order && p.display_order < 9999 && (
                      <span className="text-black/60 font-mono">#{p.display_order}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-2 text-sm">{p.category}</div>
              <div className="col-span-2 text-sm">
                {formatINR(p.price)}
                {p.original_price > p.price && (
                  <span className="text-[#5C5C5C] line-through ml-2 text-xs">{formatINR(p.original_price)}</span>
                )}
              </div>
              <div className="col-span-1 text-sm">{p.stock}</div>
              <div className="col-span-2 flex items-center gap-2 justify-end">
                <button
                  onClick={() => setEditing(p)}
                  className="w-9 h-9 border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                  data-testid={`admin-edit-${p.id}`}
                  aria-label="Edit"
                >
                  <Edit2 size={13} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => remove(p.id)}
                  className="w-9 h-9 border border-black/15 flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                  data-testid={`admin-delete-${p.id}`}
                  aria-label="Delete"
                >
                  <Trash2 size={13} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}

      {editing && (
        <ProductEditor
          product={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/* ---------------- Product Editor Modal ---------------- */
const MAX_IMAGES = 8;

function ProductEditor({ product, onClose, onSaved }) {
  const isNew = !product;
  const initialImages = product?.images && product.images.length
    ? product.images
    : product?.image
      ? [product.image]
      : [];
  const [form, setForm] = useState({
    name: product?.name || "",
    category: product?.category || "Hair Care",
    description: product?.description || "",
    price: product?.price ?? 0,
    original_price: product?.original_price ?? 0,
    images: initialImages,
    rating: product?.rating ?? 4.8,
    review_count: product?.review_count ?? 0,
    badges: (product?.badges || []).join(", "),
    stock: product?.stock ?? 100,
    bestseller: product?.bestseller || false,
    new_arrival: product?.new_arrival || false,
    tiers: product?.tiers || [],
    display_order: product?.display_order ?? 9999,
    benefits: product?.benefits || "",
    how_to_use: product?.how_to_use || "",
    ingredients: product?.ingredients || "",
    suitable_for: product?.suitable_for || "",
    faqs: product?.faqs || [],
    seo_title: product?.seo_title || "",
    seo_description: product?.seo_description || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadOne = async (file) => {
    if (!file) return null;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`"${file.name}" is over 5 MB — skipped`);
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Upload of "${file.name}" failed`);
      return null;
    }
  };

  const onFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    const slotsLeft = MAX_IMAGES - form.images.length;
    if (slotsLeft <= 0) {
      toast.error(`Max ${MAX_IMAGES} images per product`);
      return;
    }
    const accepted = files.slice(0, slotsLeft);
    if (files.length > slotsLeft) {
      toast.error(`Only first ${slotsLeft} images added (max ${MAX_IMAGES})`);
    }
    setUploading(true);
    const uploaded = [];
    for (const f of accepted) {
      const url = await uploadOne(f);
      if (url) uploaded.push(url);
    }
    if (uploaded.length) {
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} added.`);
    }
    setUploading(false);
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const setPrimary = (idx) => {
    setForm((f) => {
      if (idx === 0) return f;
      const next = [...f.images];
      const [moved] = next.splice(idx, 1);
      next.unshift(moved);
      return { ...f, images: next };
    });
  };

  const moveImage = (idx, dir) => {
    setForm((f) => {
      const next = [...f.images];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return f;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...f, images: next };
    });
  };

  const save = async () => {
    if (!form.name || !form.category || form.images.length === 0) {
      toast.error("Name, category and at least one image are required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      image: form.images[0],
      images: form.images,
      price: Number(form.price),
      original_price: Number(form.original_price) || Number(form.price),
      rating: Number(form.rating) || 4.8,
      review_count: Number(form.review_count) || 0,
      stock: Number(form.stock) || 0,
      badges: form.badges
        .split(",")
        .map((b) => b.trim())
        .filter(Boolean),
      tiers: form.tiers
        .filter((t) => Number(t.min_qty) > 0 && Number(t.price) > 0)
        .map((t) => ({
          min_qty: Number(t.min_qty),
          price: Number(t.price),
          label: t.label || `${t.min_qty} PCS`,
        })),
      display_order: Number.isFinite(Number(form.display_order)) ? Number(form.display_order) : 9999,
    };
    try {
      if (isNew) {
        await api.post("/admin/products", payload);
        toast.success("Product created.");
      } else {
        await api.put(`/admin/products/${product.id}`, payload);
        toast.success("Product updated.");
      }
      onSaved();
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/55 backdrop-blur-sm flex items-start md:items-center justify-center p-2 md:p-6 overflow-y-auto"
      data-testid="product-editor-overlay"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-4xl my-6 luxury-shadow"
        data-testid="product-editor"
      >
        <div className="flex items-center justify-between px-5 md:px-7 py-4 md:py-5 border-b border-black/5">
          <div>
            <div className="overline">{isNew ? "Create" : "Edit"}</div>
            <div className="font-serif-display text-2xl mt-1">
              {isNew ? "New Product" : product.name}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="product-editor-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 md:p-7 grid md:grid-cols-5 gap-5 md:gap-7">
          <div className="md:col-span-2">
            <div className="overline mb-3">Gallery — first image is the cover</div>

            {form.images.length > 0 ? (
              <div className="aspect-square bg-[#F8F5F0] overflow-hidden mb-3">
                <img src={absoluteUrl(form.images[0])} alt="cover" className="w-full h-full object-cover" data-testid="product-editor-cover-preview" />
              </div>
            ) : (
              <div className="aspect-square bg-[#F8F5F0] border border-dashed border-black/15 flex items-center justify-center text-[#5C5C5C] text-xs px-4 mb-3">
                <div className="text-center">
                  <ImageIcon size={32} strokeWidth={1.2} className="mx-auto mb-2 text-[#C9A961]" />
                  No images yet
                </div>
              </div>
            )}

            <div className="grid grid-cols-4 gap-2 mb-4" data-testid="product-editor-gallery">
              {form.images.map((src, idx) => (
                <div
                  key={src + idx}
                  className={`relative group aspect-square bg-[#F8F5F0] border ${idx === 0 ? "border-[#C9A961]" : "border-transparent"}`}
                  data-testid={`product-editor-gallery-item-${idx}`}
                >
                  <img src={absoluteUrl(src)} alt={`img ${idx + 1}`} className="w-full h-full object-cover" />
                  {idx === 0 && (
                    <div className="absolute top-1 left-1 bg-[#C9A961] text-white text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5">
                      Cover
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    {idx !== 0 && (
                      <button
                        onClick={() => setPrimary(idx)}
                        className="w-7 h-7 bg-white/95 text-[#C9A961] flex items-center justify-center"
                        title="Set as cover"
                        data-testid={`product-editor-set-cover-${idx}`}
                      >
                        <Star size={12} strokeWidth={1.6} />
                      </button>
                    )}
                    <button
                      onClick={() => moveImage(idx, -1)}
                      disabled={idx === 0}
                      className="w-7 h-7 bg-white/95 flex items-center justify-center disabled:opacity-30"
                      title="Move left"
                      data-testid={`product-editor-move-left-${idx}`}
                    >
                      ←
                    </button>
                    <button
                      onClick={() => moveImage(idx, 1)}
                      disabled={idx === form.images.length - 1}
                      className="w-7 h-7 bg-white/95 flex items-center justify-center disabled:opacity-30"
                      title="Move right"
                      data-testid={`product-editor-move-right-${idx}`}
                    >
                      →
                    </button>
                    <button
                      onClick={() => removeImage(idx)}
                      className="w-7 h-7 bg-white/95 text-red-600 flex items-center justify-center"
                      title="Remove"
                      data-testid={`product-editor-remove-${idx}`}
                    >
                      <Trash2 size={11} strokeWidth={1.6} />
                    </button>
                  </div>
                </div>
              ))}

              {form.images.length < MAX_IMAGES && (
                <label className="aspect-square border border-dashed border-black/20 flex items-center justify-center text-[#5C5C5C] hover:border-[#C9A961] hover:text-[#C9A961] cursor-pointer transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => onFiles(e.target.files)}
                    className="hidden"
                    data-testid="product-editor-multi-upload"
                  />
                  {uploading ? <Loader2 size={22} className="animate-spin" /> : <Plus size={26} strokeWidth={1.4} />}
                </label>
              )}
            </div>

            <div className="text-[10px] text-[#5C5C5C]">
              {form.images.length}/{MAX_IMAGES} images · JPG/PNG/WebP · 5 MB max each. Drag-and-drop coming soon — for now use the arrows / star to reorder.
            </div>

            <label className="block mt-5">
              <span className="overline mb-2 block">…or paste image URL</span>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="https://…"
                  className="flex-1 bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.currentTarget.value) {
                      if (form.images.length >= MAX_IMAGES) {
                        toast.error(`Max ${MAX_IMAGES} images`);
                        return;
                      }
                      setForm((f) => ({ ...f, images: [...f.images, e.target.value] }));
                      e.currentTarget.value = "";
                    }
                  }}
                  data-testid="product-editor-image-url"
                />
              </div>
              <div className="text-[10px] text-[#5C5C5C] mt-1">Press Enter to add</div>
            </label>
          </div>

          <div className="md:col-span-3 space-y-4">
            <Row label="Product name">
              <input
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                data-testid="product-editor-name"
              />
            </Row>
            <Row label="Category">
              <select
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                data-testid="product-editor-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="Description">
              <textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                rows={8}
                placeholder={"Write your product details — line breaks preserved exactly as you type.\n\nExample:\n\nBENEFITS\n• Deeply nourishes\n• Adds shine\n\nHOW TO USE\nApply 2-3 drops on damp hair, massage gently.\n\nINGREDIENTS\nArgan oil, Vitamin E, Rose extract"}
                className="w-full bg-white/50 border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2.5 text-sm leading-relaxed resize-y font-body"
                data-testid="product-editor-description"
              />
              <div className="text-[10px] text-[#5C5C5C] mt-1.5 uppercase tracking-wider">
                Tip: press Enter twice for a blank line · use ALL-CAPS for section headings
              </div>
            </Row>
            <div className="pt-5 mt-3 border-t border-black/10" data-testid="public-pricing-section">
              <div className="overline mb-1">🛍 Public Retail Pricing</div>
              <div className="text-[11px] text-[#5C5C5C] mb-4">What every visitor sees on the product page. MRP shows as strikethrough.</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Row label="Selling Price (₹)">
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => update("price", e.target.value)}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  data-testid="product-editor-price"
                />
              </Row>
              <Row label="MRP / Original Price (₹)">
                <input
                  type="number"
                  value={form.original_price}
                  onChange={(e) => update("original_price", e.target.value)}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  data-testid="product-editor-original-price"
                />
              </Row>
              <Row label="Stock">
                <input
                  type="number"
                  value={form.stock}
                  onChange={(e) => update("stock", e.target.value)}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  data-testid="product-editor-stock"
                />
              </Row>
              <Row label="Rating (0–5)">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  value={form.rating}
                  onChange={(e) => update("rating", e.target.value)}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  data-testid="product-editor-rating"
                />
              </Row>
            </div>
            <Row label="Badges (comma separated)">
              <input
                type="text"
                value={form.badges}
                onChange={(e) => update("badges", e.target.value)}
                placeholder="Sulfate Free, Vegan"
                className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                data-testid="product-editor-badges"
              />
            </Row>
            <div className="flex items-center gap-6 pt-2 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.bestseller}
                  onChange={(e) => update("bestseller", e.target.checked)}
                  className="accent-[#C9A961] w-4 h-4"
                  data-testid="product-editor-bestseller"
                />
                <span className="text-sm">Bestseller</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.new_arrival}
                  onChange={(e) => update("new_arrival", e.target.checked)}
                  className="accent-[#C9A961] w-4 h-4"
                  data-testid="product-editor-new-arrival"
                />
                <span className="text-sm">New Arrival</span>
              </label>
              <label className="flex items-center gap-2 ml-auto" title="Lower number shows first on Home & Shop. Use 1 to feature.">
                <span className="overline text-[10px]">Display Order</span>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => update("display_order", e.target.value)}
                  className="w-20 bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none px-2 py-1.5 text-sm"
                  data-testid="product-editor-display-order"
                  placeholder="9999"
                />
              </label>
            </div>

            <div className="pt-6 mt-4 border-t-2 border-[#C9A961]/30 bg-gradient-to-br from-[#F8F5F0]/50 to-transparent -mx-6 px-6 pb-6 -mb-6" data-testid="product-editor-tiers">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <div className="overline text-[#C9A961]">★ Salon Partner Pricing (Wholesale)</div>
                  <div className="font-serif-display text-lg tracking-tight mt-1">Bulk / Quantity Offers</div>
                  <div className="text-[11px] text-[#5C5C5C] mt-1 max-w-md">
                    These rates are <strong>visible ONLY to approved salon partners</strong>. Public visitors never see these prices.
                    <br />Example: 3 pcs @ ₹450/unit · 6 pcs @ ₹400/unit · 12 pcs @ ₹350/unit
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => update("tiers", [...form.tiers, { min_qty: "", price: "", label: "" }])}
                  className="px-3 py-1.5 border border-[#C9A961]/40 text-[10px] tracking-[0.22em] uppercase hover:bg-[#C9A961] hover:text-white transition-colors flex items-center gap-1.5"
                  data-testid="product-editor-add-tier"
                >
                  <Plus size={12} /> Add Tier
                </button>
              </div>
              {form.tiers.length === 0 ? (
                <div className="text-xs text-[#5C5C5C] italic px-4 py-5 bg-white border border-dashed border-[#C9A961]/30">
                  No salon-only bulk offers set. Add tiers above to unlock wholesale pricing for approved partners.
                </div>
              ) : (
                <div className="space-y-2">
                  {form.tiers.map((t, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`product-editor-tier-${idx}`}>
                      <input
                        type="number"
                        min="1"
                        placeholder="Min Qty"
                        value={t.min_qty}
                        onChange={(e) => {
                          const next = [...form.tiers];
                          next[idx] = { ...next[idx], min_qty: e.target.value };
                          update("tiers", next);
                        }}
                        className="col-span-3 bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm"
                        data-testid={`tier-min-qty-${idx}`}
                      />
                      <input
                        type="number"
                        min="0"
                        placeholder="₹/unit"
                        value={t.price}
                        onChange={(e) => {
                          const next = [...form.tiers];
                          next[idx] = { ...next[idx], price: e.target.value };
                          update("tiers", next);
                        }}
                        className="col-span-3 bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm"
                        data-testid={`tier-price-${idx}`}
                      />
                      <input
                        type="text"
                        placeholder="Label (e.g. 3 PCS Combo)"
                        value={t.label || ""}
                        onChange={(e) => {
                          const next = [...form.tiers];
                          next[idx] = { ...next[idx], label: e.target.value };
                          update("tiers", next);
                        }}
                        className="col-span-5 bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm"
                        data-testid={`tier-label-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => update("tiers", form.tiers.filter((_, i) => i !== idx))}
                        className="col-span-1 w-8 h-8 border border-black/15 flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors justify-self-end"
                        data-testid={`tier-remove-${idx}`}
                        aria-label="Remove tier"
                      >
                        <Trash2 size={12} strokeWidth={1.6} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rich Product Content — Tabs on PDP */}
            <div className="pt-6 mt-4 border-t border-black/10" data-testid="product-editor-content">
              <div className="overline mb-1">Product Page Tabs</div>
              <div className="text-[11px] text-[#5C5C5C] mb-4">Optional rich content shown as tabs on the product page. Leave blank to hide a tab.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Row label="Benefits">
                  <textarea
                    value={form.benefits}
                    onChange={(e) => update("benefits", e.target.value)}
                    rows={5}
                    placeholder={"• Deeply nourishes\n• Adds salon-grade shine\n• Tames frizz for 24h"}
                    className="w-full bg-white/50 border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2.5 text-sm leading-relaxed resize-y font-body"
                    data-testid="product-editor-benefits"
                  />
                </Row>
                <Row label="How to Use">
                  <textarea
                    value={form.how_to_use}
                    onChange={(e) => update("how_to_use", e.target.value)}
                    rows={5}
                    placeholder={"1. Apply 2–3 drops on damp hair\n2. Massage gently from mid-lengths\n3. Style as usual"}
                    className="w-full bg-white/50 border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2.5 text-sm leading-relaxed resize-y font-body"
                    data-testid="product-editor-how-to-use"
                  />
                </Row>
                <Row label="Ingredients">
                  <textarea
                    value={form.ingredients}
                    onChange={(e) => update("ingredients", e.target.value)}
                    rows={5}
                    placeholder={"Argan Oil, Vitamin E, Rose Extract, Aloe Vera…"}
                    className="w-full bg-white/50 border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2.5 text-sm leading-relaxed resize-y font-body"
                    data-testid="product-editor-ingredients"
                  />
                </Row>
                <Row label="Suitable For">
                  <textarea
                    value={form.suitable_for}
                    onChange={(e) => update("suitable_for", e.target.value)}
                    rows={5}
                    placeholder={"All hair types · Dry & frizzy hair · Colour-treated hair"}
                    className="w-full bg-white/50 border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2.5 text-sm leading-relaxed resize-y font-body"
                    data-testid="product-editor-suitable-for"
                  />
                </Row>
              </div>

              {/* FAQs */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="overline">FAQs</div>
                  <button
                    type="button"
                    onClick={() => update("faqs", [...form.faqs, { q: "", a: "" }])}
                    className="px-3 py-1.5 border border-black/15 text-[10px] tracking-[0.22em] uppercase hover:bg-black hover:text-white transition-colors flex items-center gap-1.5"
                    data-testid="product-editor-add-faq"
                  >
                    <Plus size={12} /> Add FAQ
                  </button>
                </div>
                {form.faqs.length === 0 ? (
                  <div className="text-xs text-[#5C5C5C] italic px-4 py-4 bg-white border border-dashed border-black/15">
                    No FAQs yet. Add a question + answer to build trust.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {form.faqs.map((f, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`product-editor-faq-${idx}`}>
                        <input
                          type="text"
                          placeholder="Question"
                          value={f.q || ""}
                          onChange={(e) => {
                            const next = [...form.faqs];
                            next[idx] = { ...next[idx], q: e.target.value };
                            update("faqs", next);
                          }}
                          className="col-span-5 bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm"
                          data-testid={`faq-q-${idx}`}
                        />
                        <textarea
                          placeholder="Answer"
                          rows={2}
                          value={f.a || ""}
                          onChange={(e) => {
                            const next = [...form.faqs];
                            next[idx] = { ...next[idx], a: e.target.value };
                            update("faqs", next);
                          }}
                          className="col-span-6 bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm resize-y"
                          data-testid={`faq-a-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => update("faqs", form.faqs.filter((_, i) => i !== idx))}
                          className="col-span-1 w-8 h-8 border border-black/15 flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors justify-self-end"
                          data-testid={`faq-remove-${idx}`}
                          aria-label="Remove FAQ"
                        >
                          <Trash2 size={12} strokeWidth={1.6} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* SEO */}
            <div className="pt-6 mt-4 border-t border-black/10" data-testid="product-editor-seo">
              <div className="overline mb-1">SEO (Search Engine)</div>
              <div className="text-[11px] text-[#5C5C5C] mb-4">Overrides the page title and meta description on Google. Leave blank to use product name + description.</div>
              <Row label="SEO Title (≤ 60 chars)">
                <input
                  type="text"
                  maxLength={70}
                  value={form.seo_title}
                  onChange={(e) => update("seo_title", e.target.value)}
                  placeholder={`${form.name || "Product"} | Blisson Professional`}
                  className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-2 text-sm"
                  data-testid="product-editor-seo-title"
                />
                <div className="text-[10px] text-[#5C5C5C] mt-1">{(form.seo_title || "").length}/60</div>
              </Row>
              <Row label="SEO Description (≤ 160 chars)">
                <textarea
                  maxLength={180}
                  rows={3}
                  value={form.seo_description}
                  onChange={(e) => update("seo_description", e.target.value)}
                  placeholder="Discover salon-grade results at home. Sulfate-free, paraben-free, cruelty-free."
                  className="w-full bg-white/50 border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm leading-relaxed resize-y"
                  data-testid="product-editor-seo-description"
                />
                <div className="text-[10px] text-[#5C5C5C] mt-1">{(form.seo_description || "").length}/160</div>
              </Row>
            </div>

          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-7 py-5 border-t border-black/5 bg-[#F8F5F0]/40">
          <button onClick={onClose} className="btn-outline" data-testid="product-editor-cancel">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary disabled:opacity-60"
            data-testid="product-editor-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving…" : isNew ? "Create Product" : "Save Changes"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="overline mb-1">{label}</div>
      {children}
    </label>
  );
}

/* ---------------- Orders ---------------- */
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expand, setExpand] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/orders");
      setOrders(r.data);
    } catch (e) {
      toast.error("Could not load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (order_id, status) => {
    try {
      await api.patch(`/admin/orders/${order_id}`, { status });
      toast.success("Order updated.");
      load();
    } catch (e) {
      toast.error("Update failed.");
    }
  };

  if (loading) return <div className="overline" data-testid="admin-orders-loading">Loading…</div>;
  if (orders.length === 0)
    return (
      <div className="text-center py-20 font-serif-display text-2xl" data-testid="admin-orders-empty">
        No orders yet.
      </div>
    );

  return (
    <div className="space-y-3" data-testid="admin-orders-tab">
      {orders.map((o) => {
        const status = STATUS_OPTIONS.find((s) => s.value === o.status) || STATUS_OPTIONS[0];
        const open = expand === o.order_id;
        return (
          <div key={o.order_id} className="border border-black/10" data-testid={`admin-order-${o.order_id}`}>
            <button
              onClick={() => setExpand(open ? null : o.order_id)}
              className="w-full px-5 py-4 grid grid-cols-12 gap-3 items-center text-left hover:bg-[#F8F5F0]/40"
              data-testid={`admin-order-toggle-${o.order_id}`}
            >
              <div className="col-span-3">
                <div className="font-mono text-xs">{o.order_id}</div>
                <div className="text-[11px] text-[#5C5C5C] mt-0.5">
                  {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </div>
              </div>
              <div className="col-span-3">
                <div className="text-sm">{o.full_name}</div>
                <div className="text-[11px] text-[#5C5C5C]">{o.email}</div>
              </div>
              <div className="col-span-2 text-sm">{o.items.length} item{o.items.length === 1 ? "" : "s"}</div>
              <div className="col-span-2 font-serif-display text-lg">{formatINR(o.total)}</div>
              <div className="col-span-2 text-right">
                <span
                  className="text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 inline-block"
                  style={{ background: status.color + "20", color: status.color }}
                >
                  {status.label}
                </span>
              </div>
            </button>
            {open && (
              <div className="px-5 py-5 border-t border-black/5 bg-[#F8F5F0]/30 grid md:grid-cols-3 gap-6">
                <div>
                  <div className="overline mb-2">Shipping</div>
                  <div className="text-sm space-y-1">
                    <div>{o.full_name}</div>
                    <div className="text-[#5C5C5C]">{o.phone}</div>
                    <div className="text-[#5C5C5C]">{o.address}</div>
                    <div className="text-[#5C5C5C]">{o.city} · {o.postal_code}</div>
                    {o.notes && <div className="text-[#5C5C5C] italic mt-2">"{o.notes}"</div>}
                  </div>
                </div>
                <div>
                  <div className="overline mb-2">Items</div>
                  <div className="space-y-2 text-sm">
                    {o.items.map((it) => (
                      <div key={it.product_id} className="flex justify-between gap-2">
                        <span className="flex-1 min-w-0 truncate">{it.name} × {it.quantity}</span>
                        <span>{formatINR(it.line_total)}</span>
                      </div>
                    ))}
                    <div className="border-t border-black/10 pt-2 flex justify-between font-medium">
                      <span>Total</span>
                      <span>{formatINR(o.total)}</span>
                    </div>
                    {o.is_slab_order && (
                      <div className="mt-3 pt-2 border-t border-[#C9A961]/40 bg-[#F8F5F0]/40 -mx-2 px-2 py-2 space-y-1" data-testid={`admin-order-slab-${o.order_id}`}>
                        <div className="overline text-[10px] text-[#C9A961] mb-1">★ Slab Order ({o.slab_tier || "—"})</div>
                        <div className="flex justify-between text-[11px] text-[#5C5C5C]">
                          <span>Cart MRP</span><span>{formatINR(o.subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-[11px] text-[#5C5C5C]">
                          <span>Slab consumed</span><span>− {formatINR(o.slab_consumed_in_order || 0)}</span>
                        </div>
                        <div className="flex justify-between text-[11px] text-[#5C5C5C]">
                          <span>Overflow MRP</span><span>{formatINR(o.overflow_mrp || 0)}</span>
                        </div>
                        <div className="flex justify-between text-[11px] text-[#5C5C5C]">
                          <span>Overflow @ {o.overflow_discount_pct || 50}% off</span><span>{formatINR(o.overflow_payable || 0)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="overline mb-2">Update Status</div>
                  <div className="grid grid-cols-2 gap-2">
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s.value}
                        onClick={() => updateStatus(o.order_id, s.value)}
                        disabled={s.value === o.status}
                        className={`px-3 py-2 text-[10px] tracking-[0.22em] uppercase border transition-colors flex items-center justify-center gap-1.5 ${
                          s.value === o.status
                            ? "bg-black text-white border-black cursor-default"
                            : "border-black/15 hover:border-black"
                        }`}
                        data-testid={`admin-order-status-${o.order_id}-${s.value}`}
                      >
                        {s.value === o.status && <CheckCircle2 size={11} />}
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {open && (
              <CourierEditor order={o} onSaved={load} />
            )}
          </div>
        );
      })}
    </div>
  );
}

const COURIER_OPTIONS = [
  { value: "", label: "—" },
  { value: "delhivery", label: "Delhivery" },
  { value: "bluedart", label: "Blue Dart" },
  { value: "dtdc", label: "DTDC" },
  { value: "ekart", label: "Ekart" },
  { value: "indiapost", label: "India Post" },
  { value: "shiprocket", label: "Shiprocket" },
  { value: "xpressbees", label: "Xpressbees" },
];

function CourierEditor({ order, onSaved }) {
  const [courier, setCourier] = useState(order.courier || "");
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || "");
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.order_id}`, {
        courier: courier || "",
        tracking_number: trackingNumber || "",
        tracking_url: trackingUrl || "",
      });
      toast.success("Tracking info saved.");
      onSaved && onSaved();
    } catch {
      toast.error("Could not save tracking info");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-5 py-5 border-t border-black/5 bg-white" data-testid={`admin-order-tracking-${order.order_id}`}>
      <div className="overline mb-3">Courier & Tracking</div>
      <div className="grid md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C] block mb-1">Courier</span>
          <select
            value={courier}
            onChange={(e) => setCourier(e.target.value)}
            className="w-full bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm"
            data-testid={`admin-order-courier-${order.order_id}`}
          >
            {COURIER_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C] block mb-1">Tracking Number</span>
          <input
            type="text"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="e.g. 1234567890"
            className="w-full bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm font-mono"
            data-testid={`admin-order-tn-${order.order_id}`}
          />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C] block mb-1">Custom Tracking URL (optional)</span>
          <input
            type="url"
            value={trackingUrl}
            onChange={(e) => setTrackingUrl(e.target.value)}
            placeholder="Auto-built from courier+TN if blank"
            className="w-full bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-sm"
            data-testid={`admin-order-url-${order.order_id}`}
          />
        </label>
      </div>
      <div className="flex items-center justify-end mt-3">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary disabled:opacity-60"
          data-testid={`admin-order-save-tracking-${order.order_id}`}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save Tracking"}
        </button>
      </div>
    </div>
  );
}
